// Question definitions for the SCS "Overall Extent of Exposure" wizard.
//
// Mirrors scsDescriptionOfStudiesQuestions / scsSubjectDispositionQuestions:
// `number` is the stable badge shown in the CSR-style index badge (page order,
// hardcoded — it does not renumber as conditional sections toggle), `text` is
// the fallback wording and `tooltip` holds the ticket INFORMATION/helper text
// (the endpoint returns wording only, no tooltip map).
//
// At runtime the wording is overridden by
// GET /clinical_summaries/safety_summary/questions, keyed by the same attribute
// the answer is stored under. `textKeys` lists the further attributes to read
// that wording from when the endpoint carries the question under another name —
// the comparison questions (Q4–Q6) are the Description of Studies questions
// repeated per grouping, so they reuse that page's wording and only their badge
// number changes.
//
// Q1 / Q2 are asked for every grouping. Q3–Q6 are only asked for the custom
// groupings added on this page: the groupings predefined on the Description of
// Studies page get their comparison setup from that page, so they stop after
// Q2 (see renderGroupingBlock).

export const SCS_OEE_QUESTIONS = {
  qExposureOtherStudies: {
    number: 1,
    text: "Do you have other completed or ongoing studies to report?",
  },
  qExposureTable: {
    number: 2,
    text: "Select the table you need to discuss.",
  },
  qExposureGroupingName: {
    number: 3,
    text: "Which additional groupings do you want to compare? Name the grouping.",
  },
  qExposureComparisonType: {
    number: 4,
    text: "What comparison are you planning to make for each group?",
  },
  // Q5 / Q6 below are the Description of Studies branch questions (its Q8 / Q9)
  // shown for the comparison type picked in Q4. The Non-Comparative names and
  // the Comparative treatment groups are two different questions on screen but
  // are saved under the same qExposureTreatmentGroups attribute (a grouping only
  // ever has one comparison type), so the Non-Comparative one keeps its own key
  // here.
  qExposureNonComparativeGroupNames: {
    number: 5,
    textKeys: ["qNonComparativeGroupNames"],
    text: "What are the actual names used to describe the treatment/analysis groups in the column headers?",
  },
  qExposureTreatmentGroups: {
    number: 5,
    text: "What are the actual names used to describe the treatment/analysis groups in the column headers? Please define the role for each column name.",
  },
  qExposureComparisonPairs: {
    number: 6,
    text: "Which dose/treatment groups should be compared?",
  },
  qExposureDoseGroups: {
    number: 5,
    text: "Please list the dose group column headers from lowest to highest, starting with Placebo if present.",
  },
};

// Comparison type (Q4) -> the value it is saved as. The question is answered
// with the on-screen wording (the same button group as Q7 of the Description of
// Studies page) but persisted as the backend's grouping kind, which also decides
// which branch answers a grouping carries:
//   Non-Comparative -> single_arm    qExposureTreatmentGroups (role left empty)
//   Comparative     -> multi_arm     qExposureTreatmentGroups ({ name, role })
//                                    + qExposureComparisonPairs
//   Dose-Finding    -> dose_finding  qExposureDoseGroups
export const SCS_OEE_COMPARISON_TYPE_VALUES = {
  "Non-Comparative": "single_arm",
  Comparative: "multi_arm",
  "Dose-Finding": "dose_finding",
};

// Saved value -> the on-screen wording, for re-populating Q4 from saved data.
export const SCS_OEE_COMPARISON_TYPE_LABELS = Object.fromEntries(
  Object.entries(SCS_OEE_COMPARISON_TYPE_VALUES).map(([label, value]) => [
    value,
    label,
  ])
);

// Intro line shown above the groupings accordion.
export const SCS_OEE_INTRO_TEXT =
  "We will report all groupings defined in Description of Studies page.";

// Yes / No toggle answers for question 1.
export const SCS_OEE_OTHER_STUDIES_OPTIONS = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];
